import React, { useRef, useState } from "react"
import "./Row.css"
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import LockIcon from "@mui/icons-material/Lock"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import { useNavigate } from "react-router-dom"

const baseURL = process.env.REACT_APP_CMS_URL

function PosterCard({ content, index }) {
  const [loaded, setLoaded] = useState(false)
  const navigate = useNavigate()

  const isSerie = content?.content_type === "serie"
  const isDraft = content?.status === "draft"
  const progress = content?.watched_percentage || 0

  const handleClick = () => {
    if (isDraft) {
      navigate("/draft", { state: { title: content?.title, publish_at: content?.publish_at } })
      return
    }
    if (isSerie) {
      navigate(`/series/${content?.id}`)
      return
    }
    navigate(`/episodes/${content?.id}`)
  }

  const draftDate = isDraft && content?.publish_at
    ? new Date(content.publish_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
    : null

  return (
    <div
      className="row__posterContainer"
      style={{ animationDelay: `${index * 45}ms` }}
      onClick={handleClick}
    >
      <span className={`row__badge ${isSerie ? "row__badge--serie" : "row__badge--episode"}`}>
        {isSerie ? "Série" : "Episódio"}
      </span>

      <img
        loading="lazy"
        src={encodeURI(`${baseURL}${content?.thumbnail?.sizes?.poster?.url}`)}
        alt={content?.thumbnail?.alt || content?.title || ""}
        className={`row__poster${loaded ? " row__poster--loaded" : ""}`}
        onLoad={() => setLoaded(true)}
        width={250}
        height={375}
      />

      <div className="row__overlay">
        <p className="row__overlay-title">{content?.title}</p>
      </div>

      {isDraft && (
        <div className="row__draftOverlay">
          <LockIcon style={{ fontSize: "1.5rem" }} />
          {draftDate && <span>Disponível em {draftDate}</span>}
        </div>
      )}

      {loaded && progress > 0 && (
        <div className="row__progress" style={{ width: `${progress * 100}%` }} />
      )}
    </div>
  )
}

function FeaturedCard({ content, index }) {
  const [loaded, setLoaded] = useState(false)
  const navigate = useNavigate()

  const isSerie = content?.content_type === "serie"
  const isDraft = content?.status === "draft"
  const progress = content?.watched_percentage || 0

  const handleClick = () => {
    if (isDraft) {
      navigate("/draft", { state: { title: content?.title, publish_at: content?.publish_at } })
      return
    }
    if (isSerie) {
      navigate(`/series/${content?.id}`)
      return
    }
    navigate(`/episodes/${content?.id}`)
  }

  const draftDate = isDraft && content?.publish_at
    ? new Date(content.publish_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
    : null

  return (
    <div
      className="row__featuredCard"
      style={{ animationDelay: `${index * 60}ms` }}
      onClick={handleClick}
    >
      <div className="row__featuredCard__poster">
        <img
          loading="lazy"
          src={encodeURI(`${baseURL}${content?.thumbnail?.sizes?.poster?.url}`)}
          alt={content?.thumbnail?.alt || content?.title || ""}
          className={`row__featuredCard__img${loaded ? " row__featuredCard__img--loaded" : ""}`}
          onLoad={() => setLoaded(true)}
          width={175}
          height={262}
        />
        {isDraft && (
          <div className="row__draftOverlay">
            <LockIcon style={{ fontSize: "1.2rem" }} />
            {draftDate && <span>Disponível em {draftDate}</span>}
          </div>
        )}
      </div>

      <div className="row__featuredCard__info">
        <div className="row__featuredCard__meta">
          <span className={`row__badge ${isSerie ? "row__badge--serie" : "row__badge--episode"}`}>
            {isSerie ? "Série" : "Episódio"}
          </span>
          <p className="row__featuredCard__title">{content?.title}</p>
          {content?.description && (
            <p className="row__featuredCard__desc">{content.description}</p>
          )}
        </div>

        <div className="row__featuredCard__footer">
          <div className="row__featuredCard__actions">
            <button className="row__featuredCard__cta">
              Retomar <ArrowForwardIcon style={{ fontSize: "0.82rem" }} />
            </button>
          </div>
          {progress > 0 && (
            <div className="row__featuredCard__progressBar">
              <div
                className="row__featuredCard__progressFill"
                style={{ width: `${progress * 100}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function Row({ index, refFunc, title, contents = [], featured = false }) {
  const scrollRef = useRef()

  const handleLeftArrow = () => {
    scrollRef.current?.scrollBy({ left: -Math.round(window.innerWidth / 2), behavior: "smooth" })
  }

  const handleRightArrow = () => {
    scrollRef.current?.scrollBy({ left: Math.round(window.innerWidth / 2), behavior: "smooth" })
  }

  const cardWidth = featured ? 514 : 270
  const shouldScroll = contents.length * cardWidth > window.innerWidth

  if (featured) {
    return (
      <div key={index} ref={refFunc} className="row">
        <h2>{title}</h2>
        {shouldScroll && (
          <>
            <div className="row__left" onClick={handleLeftArrow}>
              <NavigateBeforeIcon style={{ fontSize: 40 }} />
            </div>
            <div className="row__right" onClick={handleRightArrow}>
              <NavigateNextIcon style={{ fontSize: 40 }} />
            </div>
          </>
        )}
        <div className="row__featured" ref={scrollRef}>
          {contents.map((content, i) => (
            <FeaturedCard key={content?.id || i} content={content} index={i} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div key={index} ref={refFunc} className="row">
      <h2>{title}</h2>
      {shouldScroll && (
        <>
          <div className="row__left" onClick={handleLeftArrow}>
            <NavigateBeforeIcon style={{ fontSize: 40 }} />
          </div>
          <div className="row__right" onClick={handleRightArrow}>
            <NavigateNextIcon style={{ fontSize: 40 }} />
          </div>
        </>
      )}
      <div className="row__posters" ref={scrollRef}>
        {contents.map((content, i) => (
          <PosterCard key={content?.id || i} content={content} index={i} />
        ))}
      </div>
    </div>
  )
}

export default Row
